<template>
    <div class="space-y-6">
        <div class="rounded-lg border border-gray-200 bg-white p-6">
            <div class="flex items-center justify-between">
                <div class="flex flex-col">
                    <h3 class="m-0 p-0 text-left text-lg font-semibold text-gray-900">Import Users</h3>
                    <p class="mt-1 mb-4 text-sm text-gray-600">
                        Upload a CSV file with users you want to import.
                    </p>
                </div>

                <button @click="downloadTemplate" class="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 11l5 5l5 -5" /><path d="M12 4l0 12" /></svg>
                    Download Template
                </button>
            </div>

            <CSVFileInput
                ref="csvFileInput"
                :max-size-k-b="500"
                @file-selected="handleFileSelected"
                @file-removed="handleFileRemoved"
            />

            <div v-if="users.length > 0" class="mt-4 flex justify-end">
                <button
                    @click="save"
                    :disabled="validCount === 0 || loading"
                    class="relative w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white max-w-80 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    <span v-if="! loading">Save {{ validCount }} Users</span>
                    <span v-else class="flex items-center justify-center gap-2">
                        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                    </span>
                </button>
            </div>
        </div>

        <div v-if="error" class="flex items-center gap-2 rounded-md bg-red-50 p-4 text-red-800 border border-red-800/20">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0"> <circle cx="12" cy="12" r="10"></circle> <line x1="12" y1="8" x2="12" y2="12"></line> <line x1="12" y1="16" x2="12.01" y2="16"></line> </svg>
            <p class="mb-0 text-sm text-red-800">{{ error }}</p>
        </div>

        <div v-if="success" class="flex items-center gap-2 rounded-md bg-green-50 p-4 text-green-800 border border-green-800/20">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10" /></svg>
            <p class="mb-0 text-sm text-green-800">{{ successMessage }}</p>
        </div>

        <div v-if="users.length > 0" class="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
                <h3 class="m-0 p-0 text-left text-lg font-semibold text-gray-900">
                    Validation Results
                </h3>
                <p class="mt-1 mb-0 text-sm text-gray-600">
                    {{ validCount }} valid, {{ errorCount }} errors
                </p>
            </div>

            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                    <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Row</th>
                        <th class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">First Name</th>
                        <th class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Last Name</th>
                        <th class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Email</th>
                        <th class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Phone</th>
                        <th class="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Role</th>
                        <th class="w-96 px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                        <th class="w-40 px-6 py-3"></th>
                    </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(user, i) in users" :key="i" :class="user.errors?.length ? 'bg-red-50' : ''">

                        <td class="w-10 px-6 py-4 text-sm text-gray-500">{{ i + 1 }}</td>

                        <td class="px-6 py-4 text-sm">
                            <div class="relative w-full">
                                <input
                                    v-show="editingRow === i"
                                    v-model="user.first_name"
                                    type="text"
                                    class="absolute top-0 left-0 block w-full -translate-y-1/2 rounded bg-white px-2 text-left text-sm leading-5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 border-0 ring-1 ring-gray-200"
                                    placeholder="First Name"
                                    style="z-index:1;"
                                    @blur="validateRowOnBlur(i)"
                                />
                                <span
                                    v-show="editingRow !== i"
                                    class="block w-full px-2 text-left leading-5 text-gray-900 py-0.5"
                                >{{ user.first_name || '-' }}</span>
                            </div>
                        </td>

                        <td class="px-6 py-4 text-sm">
                            <div class="relative w-full">
                                <input
                                    v-show="editingRow === i"
                                    v-model="user.last_name"
                                    type="text"
                                    class="absolute top-0 left-0 block w-full -translate-y-1/2 rounded bg-white px-2 text-left text-sm leading-5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 border-0 ring-1 ring-gray-200"
                                    placeholder="Last Name"
                                    style="z-index:1;"
                                    @blur="validateRowOnBlur(i)"
                                />
                                <span
                                    v-show="editingRow !== i"
                                    class="block w-full px-2 text-left leading-5 text-gray-900 py-0.5"
                                >{{ user.last_name || '-' }}</span>
                            </div>
                        </td>

                        <td class="px-6 py-4 text-sm">
                            <div class="relative w-full">
                                <input
                                    v-show="editingRow === i"
                                    v-model="user.email"
                                    type="email"
                                    class="absolute top-0 left-0 block w-full -translate-y-1/2 rounded bg-white px-2 text-left text-sm leading-5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 border-0 ring-1 ring-gray-200"
                                    placeholder="email@example.com"
                                    style="z-index:1;"
                                    @blur="validateRowOnBlur(i)"
                                />
                                <span
                                    v-show="editingRow !== i"
                                    class="block w-full px-2 text-left leading-5 text-gray-900 py-0.5"
                                >{{ user.email || '-' }}</span>
                            </div>
                        </td>

                        <td class="px-6 py-4 text-sm">
                            <div class="relative w-full">
                                <input
                                    v-show="editingRow === i"
                                    v-model="user.phone"
                                    type="tel"
                                    class="absolute top-0 left-0 block w-full -translate-y-1/2 rounded bg-white px-2 text-left text-sm leading-5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 border-0 ring-1 ring-gray-200"
                                    placeholder="Phone"
                                    style="z-index:1;"
                                    @blur="validateRowOnBlur(i)"
                                />
                                <span
                                    v-show="editingRow !== i"
                                    class="block w-full px-2 text-left leading-5 text-gray-900 py-0.5"
                                >{{ user.phone || '-' }}</span>
                            </div>
                        </td>

                        <td class="px-6 py-4 text-sm">
                            <div class="relative w-full">
                                <select
                                    v-show="editingRow === i"
                                    v-model="user.role"
                                    class="absolute top-0 left-0 block w-full -translate-y-1/2 rounded bg-white px-2 text-left text-sm leading-5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 border-0 ring-1 ring-gray-200"
                                    style="z-index:1;"
                                    @blur="validateRowOnBlur(i)"
                                >
                                    <option value="">Select role</option>
                                    <option v-for="role in props.roles" :key="role" :value="role">{{ role }}</option>
                                </select>
                                <span
                                    v-show="editingRow !== i"
                                    class="block w-full px-2 text-left leading-5 text-gray-900 py-0.5"
                                >{{ user.role || '-' }}</span>
                            </div>
                        </td>

                        <td class="px-6 py-4 text-sm">
                            <div class="flex-1">
                                <span v-if="user.errors?.length" class="text-xs text-red-600">
                                    ✗ {{ user.errors.join(', ') }}
                                </span>
                                <span v-else class="text-xs text-green-600">✓ Valid</span>
                            </div>
                        </td>

                        <td class="w-40 px-6 py-4">
                            <div class="flex w-full justify-end min-h-[32px]">
                                <div v-show="editingRow === i" class="flex w-full justify-end gap-2">
                                    <button
                                        @click="saveRow(i)"
                                        class="h-8 w-full rounded border border-blue-600 bg-blue-600 text-xs text-white hover:bg-blue-700 transition-all"
                                    >
                                        Save
                                    </button>
                                    <button
                                        @click="cancelEdit"
                                        class="h-8 w-full rounded bg-gray-100 border border-gray-200 text-gray-700 text-xs  hover:bg-gray-200 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div v-show="editingRow !== i" class="flex w-full justify-end gap-2">
                                    <button
                                        @click="startEdit(i)"
                                        class="h-8 w-full rounded bg-gray-100 border border-gray-200 text-gray-700 text-xs  hover:bg-gray-200 transition-all"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        @click="deleteRow(i)"
                                        class="h-8 w-full rounded bg-red-50 border border-red-200 text-red-700 text-xs hover:bg-red-100 transition-all"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </td>

                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useValidation } from '@/composables/useValidation';
import { useCompany } from '@/composables/useCompany';
import CSVFileInput from '@/components/elements/FileInput/CSVFileInput.vue';

/**
 * @typedef {Object} ImportUser
 * @property {string} first_name - User's first name
 * @property {string} last_name - User's last name
 * @property {string} email - User's email address
 * @property {string} phone - User's phone number
 * @property {string} role - User's role
 * @property {string[]|null} errors - Array of validation error messages, null if valid
 */

const props = defineProps({
    roles: { type: Array, required: true },
    users: { type: Object, required: true }
});

const { buildCompanyRoute } = useCompany();
const { isValidEmail, isValidPhone } = useValidation();

/** @type {import('vue').Ref<ImportUser[]>} */
const users = ref([]);
const csvFileInput = ref(null);
const loading = ref(false);
const error = ref('');
const success = ref(false);
const successMessage = ref('');
const editingRow = ref(null);
const editingRowBackup = ref(null);

const existingEmailSet = new Set(
    Object.values(props.users).map(user => user.email.toLowerCase())
);
const roleSet = new Set(props.roles);

const validCount = computed(() =>
    users.value.filter(user => ! user.errors?.length).length
);

const errorCount = computed(() =>
    users.value.filter(user => user.errors?.length).length
);

const handleFileSelected = (file) => parseCSV(file);

const handleFileRemoved = () => {
    users.value = [];
    error.value = '';
    editingRow.value = null;
    editingRowBackup.value = null;
};

const parseCSV = async (csvFile) => {
    loading.value = true;
    error.value = '';

    try {
        const text = await csvFile.text();
        const lines = text.split(/\r?\n/).filter(line => line.trim());

        users.value = lines.slice(1).map(line => parseCSVLine(line));
        validateAllUsers();
    } catch (err) {
        error.value = 'Failed to parse CSV file. Please check the file format.';
        csvFileInput.value?.removeFile();
        console.error('CSV parse error:', err);
    } finally {
        loading.value = false;
    }
};

const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    result.push(current.trim());

    return {
        first_name: result[0] || '',
        last_name: result[1] || '',
        email: result[2] || '',
        phone: result[3] || '',
        role: result[4] || '',
        errors: null
    };
};

const validateAllUsers = () => {
    const emailCounts = new Map();

    users.value.forEach(user => {
        const email = user.email.toLowerCase().trim();
        if (email) {
            emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
        }
    });

    users.value.forEach((_, index) => validateRow(index, emailCounts));
};

const validateRow = (index, emailCounts = null) => {
    const user = users.value[index];
    const errors = [];
    const emailLower = user.email.toLowerCase().trim();

    if (! user.first_name) {
        errors.push('First name required');
    }

    if (! user.last_name) {
        errors.push('Last name required');
    }

    if (! user.email) {
        errors.push('Email required');
    } else if (! isValidEmail(user.email)) {
        errors.push('Invalid email');
    } else if (existingEmailSet.has(emailLower)) {
        errors.push('Email exists');
    } else if (emailCounts?.get(emailLower) > 1) {
        errors.push('Duplicate email in file');
    }

    if (user.phone && ! isValidPhone(user.phone)) {
        errors.push('Invalid phone');
    }

    if (! user.role) {
        errors.push('Role required');
    } else if (! roleSet.has(user.role)) {
        errors.push('Invalid role');
    }

    user.errors = errors.length > 0 ? errors : null;
};

const validateRowOnBlur = (index) => {
    const emailCounts = new Map();
    users.value.forEach(user => {
        const email = user.email.toLowerCase().trim();
        if (email) emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
    });

    validateRow(index, emailCounts);
};

const startEdit = (index) => {
    if (editingRow.value !== null && editingRow.value !== index) {
        validateRowOnBlur(editingRow.value);
    }

    editingRow.value = index;
    editingRowBackup.value = { ...users.value[index] };
};

const saveRow = (index) => {
    const emailCounts = new Map();
    users.value.forEach(user => {
        const email = user.email.toLowerCase().trim();
        if (email) emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
    });

    validateRow(index, emailCounts);
    editingRow.value = null;
    editingRowBackup.value = null;
};

const cancelEdit = () => {
    if (editingRow.value !== null && editingRowBackup.value !== null) {
        users.value[editingRow.value] = { ...editingRowBackup.value };
    }
    editingRow.value = null;
    editingRowBackup.value = null;
};

const deleteRow = (index) => {
    users.value.splice(index, 1);

    if (editingRow.value === index) {
        editingRow.value = null;
        editingRowBackup.value = null;
    } else if (editingRow.value > index) {
        editingRow.value--;
    }
};

const save = async () => {
    loading.value = true;
    error.value = '';
    success.value = false;

    try {
        const response = await axios.post(buildCompanyRoute('import-users'), { users: users.value });
        handleSaveResponse(response.data);
    } catch (err) {
        console.error('Import error:', err);
        error.value = err.response?.data?.message || 'Failed to save users. Please try again.';
    } finally {
        loading.value = false;
    }
};

const handleSaveResponse = (data) => {
    if (data.saved && data.users) {
        handlePartialOrFullSuccess(data.users);
    } else if (data.saved) {
        handleFullSuccess();
    } else if (data.users) {
        handleValidationFailure(data.users);
    }
};

const handlePartialOrFullSuccess = (backendUsers) => {
    const failedUsers = backendUsers.filter(user =>
        ! user.isValid || (user.errors && Object.keys(user.errors).length > 0)
    );
    const succeededCount = backendUsers.length - failedUsers.length;

    if (failedUsers.length === 0) {
        showSuccess(succeededCount);
        clearUsers();
    } else if (succeededCount > 0) {
        showPartialSuccess(succeededCount, failedUsers);
    } else {
        showAllFailed(backendUsers);
    }
};

const handleFullSuccess = () => {
    const savedCount = users.value.length;
    showSuccess(savedCount);
    clearUsers();
};

const handleValidationFailure = (backendUsers) => {
    mergeBackendErrors(backendUsers);
    error.value = 'Users failed validation. Please review the errors below and try again.';
};

const showSuccess = (count) => {
    successMessage.value = `Successfully imported ${count} user${count === 1 ? '' : 's'}.`;
    success.value = true;
    setTimeout(() => success.value = false, 5000);
};

const showPartialSuccess = (succeededCount, failedUsers) => {
    const failedEmails = new Set(failedUsers.map(user => user.email.toLowerCase()));
    users.value = users.value.filter(user => failedEmails.has(user.email.toLowerCase()));

    mergeBackendErrors(failedUsers);

    successMessage.value = `Successfully imported ${succeededCount} user${succeededCount === 1 ? '' : 's'}.`;
    success.value = true;
    error.value = `${failedUsers.length} user(s) failed validation. Please review the errors below.`;

    setTimeout(() => success.value = false, 5000);
};

const showAllFailed = (backendUsers) => {
    mergeBackendErrors(backendUsers);
    error.value = 'All users failed validation. Please review the errors below and try again.';
};

const clearUsers = () => {
    csvFileInput.value?.removeFile();
    users.value = [];
};

const mergeBackendErrors = (backendUsers) => {
    const errorMap = new Map(
        backendUsers.map(user => [
            user.email.toLowerCase(),
            user.errors && typeof user.errors === 'object' ? user.errors : null
        ])
    );

    users.value.forEach(user => {
        const backendErrors = errorMap.get(user.email.toLowerCase());
        if (backendErrors && Object.keys(backendErrors).length > 0) {
            user.errors = Object.values(backendErrors).flat();
        } else {
            user.errors = null;
        }
    });
};

const downloadTemplate = () => {
    const csv = 'First Name,Last Name,Email,Phone,Role\nPete,Crow-Armstrong,pca@cubs.com,317-287-5204,Admin';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'template.csv';
    link.click();

    URL.revokeObjectURL(url);
};
</script>